// "Your credits": what the member can book now (owner display rule,
// 2026-11-02). Data: GET /api/profile/credit-summary, the same rules as
// booking and the staff profile.
//   - Membership: credits left this period (even 0), reset date, "Paused"
//     only while today is inside the pause dates (pauseActive).
//   - Package: left, booked, expiry.
//   - Drop-ins and gifts: "N left" (no "of Y"), gift expiry.
//   - Wellness Wednesday: "N left · Wednesdays only".
//   - Credits that expired in the last 30 days: "N credits expired on <date>".
import React from "react";
import PropTypes from "prop-types";
import "./MemberCreditsCard.css";

const TZ = "America/Phoenix";

export const formatShortDate = (value, now = new Date()) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear =
    date.toLocaleDateString("en-US", { year: "numeric", timeZone: TZ }) ===
    now.toLocaleDateString("en-US", { year: "numeric", timeZone: TZ });
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: TZ,
  });
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// The main value and the detail parts for one row.
export function describeRow(row, now = new Date()) {
  const details = [];
  let value;
  if (row.category === "membership") {
    value = row.unlimited ? "Unlimited" : `${row.remaining} left this period`;
    if (row.resetsAt) details.push(`Resets ${formatShortDate(row.resetsAt, now)}`);
  } else {
    value = `${row.remaining} left`;
    if (row.wednesdayOnly) details.push("Wednesdays only");
    if (row.expiresAt) details.push(`Expires ${formatShortDate(row.expiresAt, now)}`);
    else if (row.category === "package" && row.noExpiry) details.push("No expiry");
  }
  if (row.booked > 0) details.unshift(`${row.booked} booked`);
  return { value, details };
}

const MemberCreditsCard = ({ summary, loading, error, variant, showAddOns, now }) => {
  if (loading && !summary) {
    return <p className="member-credits-muted">Loading your credits…</p>;
  }
  if (error && !summary) {
    return <p className="member-credits-muted">{error}</p>;
  }
  const rows = summary?.rows || [];
  const expired = summary?.recentlyExpired || [];
  const addOns = summary?.addOns || {};
  const addOnItems = showAddOns
    ? [
        ["Guest passes", addOns.guestPasses],
        ["Halotherapy sessions", addOns.halotherapy],
        ["Gift sessions to give", addOns.giftSessions],
      ].filter(([, count]) => count > 0)
    : [];

  return (
    <div className={`member-credits member-credits-${variant}`} data-testid="member-credits-card">
      {rows.length === 0 && addOnItems.length === 0 ? (
        <p className="member-credits-empty">No active credits</p>
      ) : (
        <ul className="member-credits-list">
          {rows.map((row) => {
            const { value, details } = describeRow(row, now);
            return (
              <li key={row.key} className="member-credits-row" data-testid={`credit-row-${row.key}`}>
                <div className="member-credits-row-main">
                  <span className="member-credits-name">{row.name}</span>
                  <span className="member-credits-value">{value}</span>
                </div>
                {(details.length > 0 || row.pauseActive || row.expiringSoon) && (
                  <div className="member-credits-details">
                    {row.pauseActive && <span className="member-credits-tag member-credits-tag-paused">Paused</span>}
                    {row.expiringSoon && <span className="member-credits-tag">Expires soon</span>}
                    {details.length > 0 && <span>{details.join(" · ")}</span>}
                  </div>
                )}
              </li>
            );
          })}
          {addOnItems.map(([label, count]) => (
            <li key={label} className="member-credits-row">
              <div className="member-credits-row-main">
                <span className="member-credits-name">{label}</span>
                <span className="member-credits-value">{count} left</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {expired.length > 0 && (
        <ul className="member-credits-expired" data-testid="member-credits-expired">
          {expired.map((item) => (
            <li key={item.key}>
              {item.name}: {plural(item.credits, "credit")} expired on {formatShortDate(item.expiredAt, now)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

MemberCreditsCard.propTypes = {
  summary: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  variant: PropTypes.oneOf(["dashboard", "sidebar", "mobile"]),
  showAddOns: PropTypes.bool,
  now: PropTypes.instanceOf(Date),
};

MemberCreditsCard.defaultProps = {
  variant: "dashboard",
  showAddOns: false,
};

export default MemberCreditsCard;
